// src/authpages/timers/timerUtils.js
import { 
  auth, 
  db, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection,
  addDoc,
  increment,
  serverTimestamp 
} from '../../firebase';

// Import the enhanced study stats service for rewards integration
import { recordStudySession as recordStudySessionWithRewards } from '../dashboard/StudyStatsService';

const STORAGE_KEY = 'studyTimerProgress';

// Predefined study session templates
const STUDY_TEMPLATES = {
  pomodoro: { studyTime: 25, breakTime: 5 },
  deepWork: { studyTime: 52, breakTime: 17 },
  energyCycle: { studyTime: 90, breakTime: 20 },
  intensiveFocus: { studyTime: 58, breakTime: 17 }
};

// Format date to YYYY-MM-DD for consistent storage
function formatDateToYYYYMMDD(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Calculate cycles based on total minutes
function calculateCycles(method, totalMinutes) {
  console.group('Calculate Cycles');
  console.log('Method:', method);
  console.log('Total Minutes:', totalMinutes);

  // Determine the cycle template
  const template = STUDY_TEMPLATES[method] || { studyTime: 25, breakTime: 5 };
  
  // Total cycle length
  const totalCycleLength = template.studyTime + template.breakTime;
  
  // Calculate full cycles
  const cycles = Math.floor(totalMinutes / totalCycleLength);

  console.log('Template Used:', template);
  console.log('Total Cycle Length:', totalCycleLength);
  console.log('Calculated Cycles:', cycles);
  console.groupEnd();

  return cycles;
}

// Validate and normalize timer method
function normalizeTimerMethod(method) {
  // Convert to lowercase and trim
  const normalizedMethod = (method || '').toLowerCase().trim();
  
  // Check if it's a predefined method
  if (Object.keys(STUDY_TEMPLATES).includes(normalizedMethod)) {
    return normalizedMethod;
  }
  
  // Default to custom if not recognized
  return 'custom';
}

// Get today's progress from localStorage
export const getTodayProgress = () => {
  try {
    const today = formatDateToYYYYMMDD(new Date());
    const savedData = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    
    console.group('Get Today\'s Progress');
    console.log('Current date:', today);
    console.log('Saved localStorage data:', savedData);
    
    // Check if we have data for today
    if (savedData.date === today) {
      console.log('Found existing data for today');
      console.groupEnd();
      return {
        sessions: savedData.sessions || 0,
        minutes: savedData.minutes || 0,
        cycles: savedData.cycles || 0
      };
    } else {
      // Reset for a new day
      console.warn('No data for today, resetting progress');
      console.groupEnd();
      return {
        sessions: 0,
        minutes: 0, 
        cycles: 0
      };
    }
  } catch (error) {
    console.error('Error getting today\'s progress:', error);
    return { sessions: 0, minutes: 0, cycles: 0 };
  }
};

// Store progress in localStorage
export const storeLocalProgress = (method, minutes) => {
  try {
    const today = formatDateToYYYYMMDD(new Date());
    
    // Normalize method and validate minutes
    const normalizedMethod = normalizeTimerMethod(method);
    const minutesNum = typeof minutes === 'number' ? 
      minutes : parseInt(minutes, 10);
    
    if (isNaN(minutesNum) || minutesNum <= 0) {
      console.error('Invalid minutes value:', minutes);
      return null;
    }
    
    // Get current progress
    const currentProgress = getTodayProgress();
    
    // Calculate new progress
    const newMinutes = currentProgress.minutes + minutesNum;
    const newCycles = calculateCycles(normalizedMethod, newMinutes);
    
    console.group('Store Local Progress');
    console.log(`Current progress:`, currentProgress);
    console.log(`Storing local progress: +${minutesNum} minutes`);
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      date: today,
      sessions: newCycles + 1, // Use cycles + 1 as sessions
      minutes: newMinutes,
      cycles: newCycles,
      method: normalizedMethod
    }));
    
    console.log(`New totals - Sessions: ${newCycles + 1}, Minutes: ${newMinutes}, Cycles: ${newCycles}`);
    console.log('Local storage updated successfully');
    console.groupEnd();
    
    return { 
      sessions: newCycles + 1, 
      minutes: newMinutes, 
      cycles: newCycles 
    };
  } catch (error) {
    console.error('Error storing progress:', error);
    return null;
  }
};

// Record a completed study session in localStorage, Firebase, and the rewards system
export const recordStudySession = async (method, minutes, options = {}) => {
  try {
    // Log authentication state for debugging
    console.group('Record Study Session');
    console.log('User auth status:', !!auth.currentUser);
    
    // Normalize method
    const normalizedMethod = normalizeTimerMethod(method);
    
    if (auth.currentUser) {
      console.log('User ID:', auth.currentUser.uid);
      console.log('User email:', auth.currentUser.email);
    } else {
      console.warn('No user authenticated. Storing in localStorage only.');
    }
    
    // First save to localStorage for immediate UI feedback
    const localStorageResult = storeLocalProgress(normalizedMethod, minutes);
    console.log('Local storage progress result:', localStorageResult);
    
    // Then update Firebase if user is authenticated
    const user = auth.currentUser;
    if (!user) {
      console.warn('No authenticated user. Session not recorded in Firebase.');
      console.groupEnd();
      return false;
    }
    
    // Ensure minutes is a number
    const minutesNum = typeof minutes === 'number' ? 
      minutes : parseInt(minutes, 10);
    
    if (isNaN(minutesNum) || minutesNum <= 0) {
      console.error('Invalid minutes value:', minutes);
      console.groupEnd();
      return false;
    }
    
    // Get today's date
    const today = formatDateToYYYYMMDD(new Date());
    console.log(`Recording study session: ${minutesNum} minutes using ${normalizedMethod} technique`);
    
    // 1. Update or create user document with studyStats field
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      // Get existing user data
      const userData = userDoc.data();
      
      // Get current study stats or initialize if needed
      const studyStats = userData.studyStats || {
        totalStudyTime: 0,
        completedSessions: 0,
        studyDays: {},
        weeklyAverage: 0,
        lastUpdated: null,
        lastResetDate: null
      };
      
      // Update user document
      await updateDoc(userRef, {
        'studyStats.totalStudyTime': increment(minutesNum),
        'studyStats.completedSessions': increment(1),
        [`studyStats.studyDays.${today}`]: increment(minutesNum),
        'studyStats.lastUpdated': serverTimestamp(),
        'studyStats.lastResetDate': today
      });
      
      console.log('Updated user document with new study stats');
    } else {
      // Create new user document with initial study stats
      const initialStudyStats = {
        totalStudyTime: minutesNum,
        completedSessions: 1,
        studyDays: { [today]: minutesNum },
        weeklyAverage: Math.round(minutesNum / 7),
        lastUpdated: serverTimestamp(),
        lastResetDate: today
      };
      
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        studyStats: initialStudyStats,
        createdAt: serverTimestamp()
      });
      
      console.log('Created new user document with initial study stats');
    }
    
    // 2. Add individual session record to studySessions collection
    const sessionData = {
      userId: user.uid,
      date: today,
      timestamp: serverTimestamp(),
      minutes: minutesNum,
      technique: normalizedMethod,
      completed: true
    };
    
    // Using collection and addDoc for studySessions
    const sessionsRef = collection(db, 'studySessions');
    const newSessionRef = await addDoc(sessionsRef, sessionData);
    
    console.log('Study session recorded successfully in Firebase with ID:', newSessionRef.id);
    
    // 3. NEW: Add rewards tracking with the enhanced study stats service
    try {
      // Extract options for rewards tracking
      const {
        hadBreaks = false,
        quizCompleted = false,
        quizDurationSeconds = 0,
        studyPartners = [],
        topicId = '',
        notes = ''
      } = options;
      
      // Create the enhanced session data object for rewards
      const rewardsSessionData = {
        // Use 'minutes' for compatibility with the original recordStudySession
        minutes: minutesNum,
        // Also add durationMinutes for compatibility with rewards system
        durationMinutes: minutesNum,
        sessionType: normalizedMethod,
        hadBreaks,
        quizCompleted,
        quizDurationSeconds,
        studyPartners,
        topicId,
        notes: notes || `${normalizedMethod} study session`,
        completedAt: new Date().toISOString()
      };
      
      console.log('Recording session for rewards tracking:', rewardsSessionData);
      
      // Call the enhanced recordStudySession from StudyStatsService
      const rewardsResult = await recordStudySessionWithRewards(rewardsSessionData);
      console.log('Rewards tracking result:', rewardsResult);
    } catch (rewardsError) {
      // Don't fail the entire function if rewards tracking fails
      console.error('Error tracking rewards (session still recorded):', rewardsError);
    }
    
    console.groupEnd();
    return true;
  } catch (error) {
    console.error('Error recording study session:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Error details:', error);
    
    // Additional error context
    try {
      console.log('Current localStorage content:', localStorage.getItem(STORAGE_KEY));
    } catch (recoveryError) {
      console.error('Could not read localStorage during error handling:', recoveryError);
    }
    
    console.groupEnd();
    return false;
  }
};

// Check for day change and reset localStorage if needed
export const checkForDayChange = () => {
  try {
    console.group('Check for Day Change');
    
    // Current date information
    const currentDate = new Date();
    const today = formatDateToYYYYMMDD(currentDate);
    console.log('Current system date:', currentDate);
    console.log('Formatted today:', today);
    
    // Retrieve localStorage data
    const localData = JSON.parse(localStorage.getItem(STORAGE_KEY));
    console.log('Stored localStorage data:', localData);
    
    // Check if there's no existing data
    if (!localData) {
      console.warn('No existing localStorage data');
      console.groupEnd();
      return false;
    }
    
    // Detailed logging of date comparison
    console.log('Stored date:', localData.date);
    console.log('Current date:', today);
    
    // Comprehensive day change detection
    const isNewDay = localData.date !== today;
    
    if (isNewDay) {
      console.warn('Day change detected! Resetting local storage.');
      
      // Create new local data for the current day
      const newLocalData = {
        date: today,
        sessions: 0,
        minutes: 0,
        cycles: 0
      };
      
      // Update localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newLocalData));
      
      console.log('New local storage data:', newLocalData);
      console.log('Local storage reset for new day');
      
      console.groupEnd();
      return true;
    }
    
    console.log('No day change detected');
    console.groupEnd();
    
    // Return false to indicate day did not change
    return false;
  } catch (error) {
    console.error('Comprehensive error in checking day change:', error);
    
    // Additional error context
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    
    // Attempt to recover or log additional context
    try {
      console.log('Current localStorage content:', localStorage.getItem(STORAGE_KEY));
    } catch (recoveryError) {
      console.error('Could not even read localStorage:', recoveryError);
    }
    
    return false;
  }
};

export const resetProgress = () => {
  try {
    const today = formatDateToYYYYMMDD(new Date());
    
    // Create reset data
    const resetData = {
      date: today,
      sessions: 0,
      minutes: 0,
      cycles: 0
    };
    
    // Reset localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(resetData));
    
    console.log('Progress tracking reset successfully');
    return true;
  } catch (error) {
    console.error('Error resetting progress:', error);
    return false;
  }
};

export default {
  formatDateToYYYYMMDD,
  getTodayProgress,
  storeLocalProgress,
  recordStudySession,
  checkForDayChange
};